import { apiFetch } from './client';

export type DtmfScope = 'platform' | 'reseller' | 'customer' | 'caller';

export interface DtmfAction {
  action: string;
  label: string;
  description?: string;
}

export type DtmfMap = Record<string, string>;

export interface NodeTypeCatalogEntry {
  node_type: string;
  label: string;
  default_map: DtmfMap;
  action_catalog: Record<string, DtmfAction>;
}

export interface DtmfCatalogResponse {
  node_types: NodeTypeCatalogEntry[];
}

export interface DtmfProfileRow {
  id: string;
  scope: DtmfScope;
  node_type: string;
  reseller_id: string | null;
  customer_id: string | null;
  caller_profile_id: string | null;
  auto_bookmark: boolean;
  speed_min: number;
  speed_max: number;
  speed_step: number;
  seek_short_sec: number;
  seek_long_sec: number;
  seek_xlong_sec: number;
  seek_9_sec: number;
  dtmf_mappings: DtmfMap;
  created_at: string;
  updated_at: string;
}

export interface EffectiveDtmfProfile {
  node_type: string;
  dtmf_mappings: DtmfMap;
  auto_bookmark: boolean;
  speed_min: number;
  speed_max: number;
  speed_step: number;
  seek_short_sec: number;
  seek_long_sec: number;
  seek_xlong_sec: number;
  seek_9_sec: number;
  source: 'default' | 'platform' | 'reseller' | 'customer' | 'caller';
}

export interface DtmfListRow {
  node_type: string;
  label: string;
  default_map: DtmfMap;
  effective_map: DtmfMap;
  my_override: DtmfMap | null;
  source: string;
  scope: DtmfScope | null;
}

export interface DtmfListResponse {
  rows: DtmfListRow[];
  scope: { resellerId: string | null; customerId: string | null };
}

export interface DtmfDetailResponse {
  node_type: string;
  label: string;
  default_map: DtmfMap;
  action_catalog: Record<string, DtmfAction>;
  effective: EffectiveDtmfProfile;
  my_override: DtmfProfileRow | null;
  scope: DtmfScope | null;
}

export interface UpsertDtmfBody {
  dtmf_mappings: DtmfMap;
  auto_bookmark?: boolean;
  speed_min?: number;
  speed_max?: number;
  speed_step?: number;
  seek_short_sec?: number;
  seek_long_sec?: number;
  seek_xlong_sec?: number;
  seek_9_sec?: number;
}

function qs(p: { reseller_id?: string; customer_id?: string } = {}): string {
  const q = new URLSearchParams();
  if (p.reseller_id) q.set('reseller_id', p.reseller_id);
  if (p.customer_id) q.set('customer_id', p.customer_id);
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const dtmfProfiles = {
  catalog: () => apiFetch<DtmfCatalogResponse>('/api/settings/dtmf-profiles/catalog'),

  list: (p: { reseller_id?: string; customer_id?: string } = {}) =>
    apiFetch<DtmfListResponse>(`/api/settings/dtmf-profiles${qs(p)}`),

  get: (nodeType: string, p: { reseller_id?: string; customer_id?: string } = {}) =>
    apiFetch<DtmfDetailResponse>(`/api/settings/dtmf-profiles/${encodeURIComponent(nodeType)}${qs(p)}`),

  put: (nodeType: string, body: UpsertDtmfBody, p: { reseller_id?: string; customer_id?: string } = {}) =>
    apiFetch<DtmfProfileRow>(`/api/settings/dtmf-profiles/${encodeURIComponent(nodeType)}${qs(p)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  reset: (nodeType: string, p: { reseller_id?: string; customer_id?: string } = {}) =>
    apiFetch<void>(`/api/settings/dtmf-profiles/${encodeURIComponent(nodeType)}${qs(p)}`, {
      method: 'DELETE',
    }),
};
